import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, MessageSquare, AlertTriangle, CheckCheck, ChevronRight, X } from "lucide-react";
import api from "@/lib/api";

const POLL_MS = 20000;

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Strip any "(n) " prefix we added earlier, then add the current one.
function setTitleCount(n) {
  const base = document.title.replace(/^\(\d+\+?\)\s+/, "");
  document.title = n > 0 ? `(${n > 99 ? "99+" : n}) ${base}` : base;
}

/**
 * Admin notifications bell: new client messages (and, in Ops, unacknowledged
 * system alerts). Polls every 20 s and when the tab regains focus.
 * "Seen" here is admin-only (its own collection on the server): opening the bell,
 * viewing the list, dismissing or "mark all as seen" never changes a message's
 * read flag, so nothing changes on the client's side.
 * variant: "admin" (light/dark app theme) | "ops" (ops palette).
 */
export default function NotificationBell({ variant = "admin", alerts = 0 }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const alive = useRef(true);

  const fetchNotes = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notifications?limit=15");
      if (!alive.current) return;
      setUnread(data.unread || 0);
      setItems(data.items || []);
      setLoaded(true);
    } catch (e) { /* silent: not admin / offline */ }
  }, []);

  useEffect(() => {
    alive.current = true;
    fetchNotes();
    const iv = setInterval(fetchNotes, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") fetchNotes(); };
    const onRefresh = () => fetchNotes();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("mentorai:notifications-refresh", onRefresh);
    return () => {
      alive.current = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("mentorai:notifications-refresh", onRefresh);
      setTitleCount(0);
    };
  }, [fetchNotes]);

  useEffect(() => { if (open) fetchNotes(); }, [open, fetchNotes]);

  const total = unread + (alerts || 0);
  useEffect(() => { setTitleCount(unread); }, [unread]);

  const openThread = (uid) => {
    setOpen(false);
    navigate(`/admin/messages?thread=${encodeURIComponent(uid)}`);
  };

  const markAllSeen = async () => {
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, new: false })));
    try { await api.post("/admin/notifications/seen-all", {}); } catch (e) { /* ignore */ }
    fetchNotes();
  };

  const dismiss = async (id) => {
    setItems((prev) => prev.map((n) => (n.message_id === id ? { ...n, new: false } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post("/admin/notifications/dismiss", { message_id: id }); } catch (e) { /* ignore */ }
    fetchNotes();
  };

  const ops = variant === "ops";
  const btn = ops
    ? "relative size-7 rounded-md flex items-center justify-center text-[hsl(var(--ops-text-muted))] hover:bg-[hsl(var(--ops-surface))] transition-colors duration-150"
    : "relative size-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors duration-200 text-muted-foreground";
  const panel = ops
    ? "bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]"
    : "";
  const muted = ops ? "text-[hsl(var(--ops-text-muted))]" : "text-muted-foreground";
  const divider = ops ? "border-[hsl(var(--ops-border))]" : "border-border";
  const hover = ops ? "hover:bg-[hsl(var(--ops-surface-2))]" : "hover:bg-muted";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" aria-label={`Notifications${total ? `: ${total} new` : ""}`} className={btn}>
          <Bell className={ops ? "size-3.5" : "size-4"} />
          {total > 0 && (
            <span data-testid="notif-badge"
              className={`absolute ${ops ? "-top-1 -right-1" : "top-0.5 right-0.5"} min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] leading-none flex items-center justify-center font-bold ring-2 ${ops ? "ring-[hsl(var(--ops-bg))]" : "ring-background"}`}>
              {total > 9 ? "9+" : total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} data-testid="notif-panel"
        className={`p-0 w-[min(22rem,calc(100vw-1.5rem))] rounded-xl overflow-hidden ${panel}`}>
        <div className={`flex items-center justify-between px-4 py-2.5 border-b ${divider}`}>
          <div className="text-sm font-semibold">Notifications</div>
          {unread > 0 && (
            <button data-testid="notif-mark-all" onClick={markAllSeen} className={`flex items-center gap-1 text-[11px] ${muted} hover:text-brand`}>
              <CheckCheck className="size-3.5" /> Mark all as seen
            </button>
          )}
        </div>

        {ops && alerts > 0 && (
          <button onClick={() => { setOpen(false); navigate("/ops/logs"); }} data-testid="notif-alerts-row"
            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b ${divider} ${hover}`}>
            <span className="size-7 rounded-full bg-red-500/15 text-red-500 flex items-center justify-center shrink-0"><AlertTriangle className="size-3.5" /></span>
            <span className="flex-1 text-[13px]">{alerts} system alert{alerts === 1 ? "" : "s"} to review</span>
            <ChevronRight className={`size-4 ${muted}`} />
          </button>
        )}

        <div className="max-h-[min(24rem,60vh)] overflow-y-auto">
          {!loaded ? (
            <div className={`px-4 py-6 text-center text-xs ${muted}`}>Loading…</div>
          ) : items.length === 0 ? (
            <div className={`px-4 py-8 text-center text-xs ${muted} flex flex-col items-center gap-2`}>
              <MessageSquare className="size-6 opacity-40" /> No client messages yet
            </div>
          ) : items.map((n) => (
            <div key={n.message_id} data-testid={`notif-item-${n.message_id}`}
              className={`group flex items-start gap-3 px-4 py-3 border-b last:border-b-0 ${divider} ${n.new ? (ops ? "bg-[hsl(var(--ops-brand))]/[0.06]" : "bg-brand/[0.05]") : ""}`}>
              <span className={`mt-1.5 size-2 rounded-full shrink-0 ${n.new ? "bg-brand" : "bg-transparent"}`} />
              <span className="flex-1 min-w-0">
                <span className="flex items-baseline justify-between gap-2">
                  <span className={`text-[13px] truncate ${n.new ? "font-semibold" : ""}`}>{n.user_name}</span>
                  <span className={`text-[11px] shrink-0 ${muted}`}>{timeAgo(n.timestamp)}</span>
                </span>
                <span className={`block text-xs mt-0.5 line-clamp-2 break-words ${muted}`}>{n.preview}</span>
                <span className="flex items-center gap-3 mt-1.5">
                  <button data-testid={`notif-open-${n.message_id}`} onClick={() => openThread(n.user_id)}
                    className="text-[11px] font-medium text-brand hover:underline">Open chat</button>
                  {n.new && (
                    <button data-testid={`notif-dismiss-${n.message_id}`} onClick={() => dismiss(n.message_id)}
                      className={`flex items-center gap-0.5 text-[11px] ${muted} hover:text-brand`}><X className="size-3" /> Dismiss</button>
                  )}
                </span>
              </span>
            </div>
          ))}
        </div>

        <button onClick={() => { setOpen(false); navigate("/admin/messages"); }} data-testid="notif-open-messages"
          className={`w-full px-4 py-2.5 text-center text-xs font-medium text-brand border-t ${divider} ${hover}`}>
          Open Messages
        </button>
      </PopoverContent>
    </Popover>
  );
}
